import { Injectable } from '@angular/core';
import { AccountInfo, AuthenticationResult, SilentRequest } from '@azure/msal-browser';
import { MsalService } from '@azure/msal-angular';

import { AuthenticationStage } from '@core/messages';
import { securityLog } from '../helpers';
import { AccountContextService } from './account-context.service';
import { AuthAccountService } from './auth-account.service';
import { B2cAuthorityService } from './b2c-authority.service';
import { ProtectedResourceService } from './protected-resource.service';
import { TenantClaimResolverService } from './tenant-claim-resolver.service';

@Injectable({
    providedIn: 'root'
})
export class AuthSessionService {
    private msalInitializePromise?: Promise<void>;

    constructor(
        private msalService: MsalService,
        private accountContext: AccountContextService,
        private accountService: AuthAccountService,
        private authorityService: B2cAuthorityService,
        private protectedResourceService: ProtectedResourceService,
        private tenantClaimResolver: TenantClaimResolverService,
    ) { }

    public ensureMsalInitialized(): Promise<void> {
        if (!this.msalInitializePromise) {
            this.msalInitializePromise = this.msalService.instance.initialize();
        }

        return this.msalInitializePromise;
    }

    public resolveSilentRequest(scopes: string[], preferredAccount?: AccountInfo): SilentRequest | undefined {
        const account = this.accountContext.resolveBestAccount(
            preferredAccount || this.accountService.getActiveAccount(),
            this.accountService.getAllAccounts(),
        );

        if (!account) {
            return undefined;
        }

        const authority = this.accountContext.resolveAuthorityForAccount(account, this.authorityService.signUpSignInAuthority);
        const request: SilentRequest = { scopes, account };

        if (authority) {
            request.authority = authority;
        }

        return request;
    }

    public async acquireApplicationApiTokenResult(preferredAccount?: AccountInfo): Promise<{ result: AuthenticationResult; authority?: string } | undefined> {
        const protectedResources = this.protectedResourceService.getProtectedResources();
        const request = this.resolveSilentRequest(protectedResources.applicationApi.scopes.read, preferredAccount);
        if (!request) {
            securityLog.error('AuthSessionService: No active account found for token acquisition');
            return undefined;
        }

        const result = await this.msalService.instance.acquireTokenSilent(request);
        this.accountContext.updateAuthContextFromResult(result, request.authority);
        return {
            result,
            authority: request.authority,
        };
    }

    public async restoreAuthenticatedSession(account: AccountInfo): Promise<{ result: AuthenticationResult; tenant?: string; authority?: string; stage: AuthenticationStage } | undefined> {
        await this.ensureMsalInitialized();

        const protectedResources = this.protectedResourceService.getProtectedResources();
        const request = this.resolveSilentRequest([
            ...protectedResources.applicationApi.scopes.read,
            ...protectedResources.applicationApi.scopes.write,
        ], account);

        if (!request) {
            securityLog.warn('AuthSessionService: Silent token restoration skipped: no account context could be resolved');
            return undefined;
        }

        const result = await this.msalService.instance.acquireTokenSilent(request);
        this.accountService.setActiveAccount(result.account);
        this.accountContext.updateAuthContextFromResult(result, request.authority);
        this.accountContext.persistPinnedAccountInformationFromResult(result, request.authority);

        return {
            result,
            tenant: this.tenantClaimResolver.resolveTenantFromAuthResult(result),
            authority: request.authority,
            stage: AuthenticationStage.AuthRestoring,
        };
    }

    public async resolveIdTokenHint(account: AccountInfo): Promise<{ idToken: string; result: AuthenticationResult; authority?: string } | undefined> {
        await this.ensureMsalInitialized();

        const protectedResources = this.protectedResourceService.getProtectedResources();
        const request = this.resolveSilentRequest([
            ...protectedResources.applicationApi.scopes.read,
            ...protectedResources.applicationApi.scopes.write,
        ], account);

        if (!request) {
            securityLog.error('AuthSessionService: cannot resolve silent request for tenant switch id token hint');
            return undefined;
        }

        const result = await this.msalService.instance.acquireTokenSilent(request);
        this.accountContext.updateAuthContextFromResult(result, request.authority);

        const idToken = (result.idToken || '').trim();
        if (!idToken) {
            return undefined;
        }

        return {
            idToken,
            result,
            authority: request.authority,
        };
    }
}